const DB_NAME = 'roam-costa-rica'
const DB_VERSION = 1
const STORE = 'photos'

export interface StoredPhoto {
  /** Discoverable id: an attraction, settlement, or history event. */
  id: string
  placeId: string
  subject: string
  caption: string
  takenAt: string
  width: number
  height: number
  bytes: number
  full: Blob
  thumb: Blob
}

export type PutOutcome = 'stored' | 'quota' | 'unavailable' | 'failed'

/** Used when IndexedDB is blocked, as in some private-browsing modes. */
const memory = new Map<string, StoredPhoto>()
let dbPromise: Promise<IDBDatabase | null> | null = null
let unavailable = false

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function openDb(): Promise<IDBDatabase | null> {
  if (unavailable) return Promise.resolve(null)
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      unavailable = true
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      unavailable = true
      resolve(null)
    }
    req.onblocked = () => resolve(null)
  })
  return dbPromise
}

export async function isPhotoStoreReady(): Promise<boolean> {
  return (await openDb()) !== null
}

/**
 * Asks the browser to exempt the compendium from eviction under storage pressure.
 * Safe to call repeatedly; browsers may grant silently, prompt, or refuse.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false
    if (await navigator.storage.persisted?.()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export async function putPhoto(photo: StoredPhoto): Promise<PutOutcome> {
  const db = await openDb()
  if (!db) {
    memory.set(photo.id, photo)
    return 'unavailable'
  }
  releaseUrl(photo.id)
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(photo)
      tx.oncomplete = () => resolve()
      tx.onabort = () => reject(tx.error)
      tx.onerror = () => reject(tx.error)
    })
    return 'stored'
  } catch (error) {
    memory.set(photo.id, photo)
    const name = (error as DOMException | null)?.name
    return name === 'QuotaExceededError' ? 'quota' : 'failed'
  }
}

export async function getPhoto(id: string): Promise<StoredPhoto | undefined> {
  const db = await openDb()
  if (!db) return memory.get(id)
  try {
    const tx = db.transaction(STORE, 'readonly')
    return (await request(tx.objectStore(STORE).get(id))) as StoredPhoto | undefined
  } catch {
    return memory.get(id)
  }
}

export async function listPhotoIds(): Promise<string[]> {
  const db = await openDb()
  if (!db) return [...memory.keys()]
  try {
    const tx = db.transaction(STORE, 'readonly')
    const keys = await request(tx.objectStore(STORE).getAllKeys())
    return keys.map(String)
  } catch {
    return [...memory.keys()]
  }
}

export async function deletePhoto(id: string): Promise<void> {
  releaseUrl(id)
  memory.delete(id)
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(id)
  } catch {
    // The ledger is the source of truth for what exists; a failed delete is not fatal.
  }
}

/**
 * Object URLs are cached because the Atlas re-renders its list on every tab
 * switch and minting a fresh URL per row leaks one blob handle each time.
 */
const urlCache = new Map<string, string>()

export async function getThumbUrl(id: string): Promise<string | undefined> {
  const cached = urlCache.get(id)
  if (cached) return cached
  const photo = await getPhoto(id)
  if (!photo) return undefined
  const url = URL.createObjectURL(photo.thumb)
  urlCache.set(id, url)
  return url
}

export async function getFullBlob(id: string): Promise<Blob | undefined> {
  return (await getPhoto(id))?.full
}

function releaseUrl(id: string) {
  const url = urlCache.get(id)
  if (!url) return
  URL.revokeObjectURL(url)
  urlCache.delete(id)
}

export function releaseAllUrls(): void {
  for (const url of urlCache.values()) URL.revokeObjectURL(url)
  urlCache.clear()
}

export interface StorageUsage {
  usage: number
  quota: number
  photos: number
}

export async function estimateUsage(): Promise<StorageUsage | null> {
  try {
    const ids = await listPhotoIds()
    const estimate = await navigator.storage?.estimate?.()
    if (!estimate) return null
    return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0, photos: ids.length }
  } catch {
    return null
  }
}
